/* ════════════════════════════════════════════════════════════════
   Helpers for the menu admin tree.

   Data comes straight from decoupled_toolbox's generic endpoints:
   - read  : /decoupled-api/menu_link_content/<bundle>/collection
   - write : decoupledSave / decoupledDelete (existing generic helpers)

   No custom Drupal module, no menu-specific api.js functions.
   ════════════════════════════════════════════════════════════════ */

const CONTENT_PREFIX = 'menu_link_content:';

/**
 * Normalize one collection row into a flat tree item.
 * The `decoupled` view display must expose: uuid, title, link, weight,
 * parent, enabled (see docs/frontend/menu-admin.md).
 */
function transformOne(row) {
  const d = row?.data ?? row ?? {};
  const uuid = d.uuid || '';
  // link exposed via decoupled_generic_raw → raw { uri, title, options }
  // (handle array cardinality and the decoupled_link {url} shape as fallback).
  const linkRaw = Array.isArray(d.link) ? d.link[0] : d.link;
  const uri = (linkRaw && (linkRaw.uri ?? linkRaw.url)) || '';
  const parent = typeof d.parent === 'string' ? d.parent : (d.parent?.[0] || '');

  return {
    id: d.id != null ? parseInt(d.id, 10) : null,
    uuid,
    key: uuid ? `${CONTENT_PREFIX}${uuid}` : `id-${d.id}`,
    parentKey: parent || '',
    title: d.title || 'Sans titre',
    uri,
    description: d.description || '',
    weight: parseInt(d.weight, 10) || 0,
    enabled: d.enabled !== false && d.enabled !== 0 && d.enabled !== '0',
  };
}

export function transformMenuItems(rows) {
  return (rows || []).filter(Boolean).map(transformOne);
}

/**
 * Build the nested TreeItems structure (dnd-kit-sortable-tree) from the flat
 * list. Every link is a content link here, so all are draggable.
 */
export function buildMenuTreeItems(flatItems, expandedKeys) {
  const byParent = new Map();
  for (const item of flatItems) {
    const pk = item.parentKey || '';
    if (!byParent.has(pk)) byParent.set(pk, []);
    byParent.get(pk).push(item);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => (a.weight ?? 0) - (b.weight ?? 0));
  }

  const build = (parentKey) => {
    const children = byParent.get(parentKey) || [];
    return children.map((item) => {
      const childNodes = build(item.key);
      return {
        id: item.key,
        _item: item,
        children: childNodes,
        collapsed: childNodes.length > 0 && !expandedKeys.has(item.key),
        canHaveChildren: true,
      };
    });
  };

  return build('');
}

/**
 * Walk the dnd tree and return the links whose parent or weight changed,
 * ready for individual decoupledSave calls.
 *
 * @param {Array} treeItems
 * @param {string} parentKey - parent plugin id ('' at the root).
 * @returns {{ id: number, parent: string, weight: number }[]}
 */
export function extractChanges(treeItems, parentKey = '') {
  const out = [];
  treeItems.forEach((node, index) => {
    const item = node._item;
    if (item?.id != null) {
      const changedParent = (item.parentKey || '') !== parentKey;
      const changedWeight = (item.weight ?? 0) !== index;
      if (changedParent || changedWeight) {
        out.push({ id: item.id, parent: parentKey, weight: index });
      }
    }
    if (node.children?.length) {
      out.push(...extractChanges(node.children, item.key));
    }
  });
  return out;
}

/**
 * After a drag-drop, return a new tree whose every node._item carries its new
 * parentKey + weight (= sibling index). Lets us keep state consistent without
 * refetching from the server. Call AFTER extractChanges (which needs the old
 * meta to diff).
 */
export function syncItemsMeta(items, parentKey = '') {
  return items.map((node, index) => ({
    ...node,
    _item: { ...node._item, parentKey, weight: index },
    children: node.children?.length ? syncItemsMeta(node.children, node._item.key) : node.children,
  }));
}

/** Return a new tree with the node whose _item.id === id patched. */
export function patchNode(items, id, patch) {
  return items.map((node) => {
    if (node._item?.id === id) {
      return { ...node, _item: { ...node._item, ...patch } };
    }
    if (node.children?.length) {
      return { ...node, children: patchNode(node.children, id, patch) };
    }
    return node;
  });
}

/** Map every node (depth-first), returning a new tree. */
function mapAllNodes(items, fn) {
  return items.map((node) => {
    const next = node.children?.length ? { ...node, children: mapAllNodes(node.children, fn) } : node;
    return fn(next);
  });
}

/** Collapse or expand every node that has children. */
export function collapseAll(items, collapsed) {
  return mapAllNodes(items, (node) => (node.children?.length ? { ...node, collapsed } : node));
}

/** Collapse/expand a node and all its descendants (subtree rooted at rootKey). */
export function collapseSubtree(items, rootKey, collapsed) {
  const deep = (node) => ({
    ...node,
    collapsed: node.children?.length ? collapsed : node.collapsed,
    children: node.children?.length ? node.children.map(deep) : node.children,
  });
  return items.map((node) => {
    if (node._item?.key === rootKey) return deep(node);
    if (node.children?.length) return { ...node, children: collapseSubtree(node.children, rootKey, collapsed) };
    return node;
  });
}

/** Normalize a user-entered URL into a valid Drupal link `uri`. */
export function normalizeUri(input) {
  const v = (input || '').trim();
  if (!v) return '';
  if (v === '<front>') return 'internal:/';
  if (v === '<nolink>' || v === 'route:<nolink>') return 'route:<nolink>';
  if (/^(https?:|mailto:|tel:|internal:|entity:|route:|base:)/.test(v)) return v;
  if (v.startsWith('/')) return `internal:${v}`;
  return `internal:/${v}`;
}

/** Convert a stored `uri` back to a human-friendly value for editing. */
export function displayUri(uri) {
  const v = (uri || '').trim();
  if (!v) return '';
  if (v === 'internal:/') return '<front>';
  if (v.startsWith('internal:')) return v.slice('internal:'.length);
  return v;
}
